import { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { useSendMessage } from "../api/useAdminEmails";
import { useToast } from "@/components/ux/Toast";

interface SendMessageButtonProps {
  /** Array of user IDs to send the message to */
  userIds?: string[];
  /** Table ID to send message to all users on the table */
  tableId?: string;
  /** Display label for the recipients (e.g., "John Doe", "Table 1 at Restaurant", "3 participants") */
  recipientLabel: string;
  /** Optional additional context shown in the modal */
  recipientDetails?: string;
  /** Button variant */
  variant?: "icon" | "button" | "compact";
  /** Custom class name for the button */
  className?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
}

export function SendMessageButton({
  userIds,
  tableId,
  recipientLabel,
  recipientDetails,
  variant = "icon",
  className,
  disabled = false,
}: SendMessageButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const sendMessage = useSendMessage();
  const toast = useToast();

  const isDisabled = disabled || (!userIds?.length && !tableId);

  const handleSend = async () => {
    if (sendMessage.isPending || !message.trim()) return;

    try {
      const result = await sendMessage.mutateAsync({
        userIds,
        tableId,
        message: message.trim(),
      });

      if (result.success) {
        toast.success(result.message || "Message sent successfully");
        setShowModal(false);
        setMessage("");
      } else {
        toast.error(result.error || "Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
      console.error("Failed to send message:", error);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setMessage("");
  };

  const renderButton = () => {
    const baseClasses = "transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
    
    switch (variant) {
      case "icon":
        return (
          <button
            onClick={(evt) => { evt.stopPropagation(); setShowModal(true); }}
            disabled={isDisabled}
            className={`btn btn-ghost btn-sm p-2 text-primary-500 hover:text-primary-600 ${baseClasses} ${className ?? ""}`}
            title={isDisabled ? "No recipients" : `Send SMS to ${recipientLabel}`}
          >
            <Send className="w-4 h-4" />
          </button>
        );
      case "compact":
        return (
          <button
            onClick={() => setShowModal(true)}
            disabled={isDisabled}
            className={`flex items-center gap-2 px-3 py-1.5 text-xs bg-primary-500/20 hover:bg-primary-500/30 rounded-lg text-primary-500 ${baseClasses} ${className ?? ""}`}
            title={isDisabled ? "No recipients" : `Send message to ${recipientLabel}`}
          >
            <Send className="w-3.5 h-3.5" />
            <span>Send SMS</span>
          </button>
        );
      case "button":
      default:
        return (
          <button
            onClick={() => setShowModal(true)}
            disabled={isDisabled}
            className={`flex items-center gap-2 px-3 py-1.5 bg-primary-500/20 text-primary-400 rounded-lg text-sm font-medium hover:bg-primary-500/30 ${baseClasses} ${className ?? ""}`}
            title={isDisabled ? "No recipients" : `Send message to ${recipientLabel}`}
          >
            <Send className="w-4 h-4" />
            Send SMS
          </button>
        );
    }
  };

  return (
    <>
      {renderButton()}

      {/* Message Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <div
            className="card max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2 dark:text-neutral-100">
              Send Message
            </h3>
            <p className="text-sm text-dark-muted dark:text-neutral-400 mb-1">
              To: {recipientLabel}
            </p>
            {recipientDetails && (
              <p className="text-xs text-dark-muted/70 dark:text-neutral-500 mb-4">
                {recipientDetails}
              </p>
            )}
            {!recipientDetails && <div className="mb-4" />}
            
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message..."
              className="w-full h-32 px-4 py-3 rounded-lg bg-dark-border/30 dark:bg-neutral-700 border border-dark-border/50 dark:border-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:text-neutral-100 dark:placeholder-neutral-400 text-sm resize-none"
              autoFocus
            />
            
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-dark-muted dark:text-neutral-400 hover:text-dark-text dark:hover:text-neutral-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={!message.trim() || sendMessage.isPending}
                className="px-4 py-2 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {sendMessage.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
